import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { MaskLines } from "../site/Reveal";

const SLIDES = [
  { img: "/images/w-cows.jpg", label: "Ruminant nutrition", range: "PALATIVA", slug: "palativa", text: "Flavours & sweeteners for cattle and buffalo", lines: ["Make every mouthful", "inviting."], sub: "Flavours and sweeteners that bring cattle and buffalo to the trough sooner, mask bitter ingredients and keep them eating." },
  { img: "/images/w-fishfarm.jpg", label: "Aquaculture", range: "OPTIBITE", slug: "optibite", text: "Attractants for fish & shrimp feed", lines: ["Make every bite", "count."], sub: "Attractant cues that help fish and shrimp find feed faster and finish it before it breaks down or leaches." },
  { img: "/images/w-poultry.jpg", label: "Poultry performance", range: "AVITIVA", slug: "avitiva", text: "Pigmentation, fat, moisture & curcumin", lines: ["More from every", "gram of feed."], sub: "Golden yolk colour, better fat utilisation, balanced feed moisture and standardised curcumin for broilers, layers and breeders." },
];

const DURATION = 7000;

export const CorpHero = () => {
  const [i, setI] = useState(0);
  const { scrollY } = useScroll();
  const y = useTransform(scrollY, [0, 900], [0, 180]);
  const scale = useTransform(scrollY, [0, 900], [1, 1.08]);

  useEffect(() => {
    const t = setTimeout(() => setI((v) => (v + 1) % SLIDES.length), DURATION);
    return () => clearTimeout(t);
  }, [i]);

  const s = SLIDES[i];

  return (
    <section data-testid="hero-section" className="relative h-[100svh] min-h-[640px] max-h-[980px] overflow-hidden bg-[var(--forest)]">
      <motion.div style={{ y, scale }} className="absolute inset-0">
        {SLIDES.map((sl, k) => (
          <motion.img
            key={sl.img}
            src={sl.img}
            alt={sl.label}
            initial={false}
            animate={{ opacity: k === i ? 1 : 0, scale: k === i ? 1 : 1.08 }}
            transition={{ opacity: { duration: 1.2 }, scale: { duration: DURATION / 1000 + 1.2, ease: "linear" } }}
            className="absolute inset-0 h-full w-full object-cover"
          />
        ))}
      </motion.div>
      <div className="absolute inset-0 bg-[linear-gradient(90deg,rgba(0,40,26,0.92)_0%,rgba(0,40,26,0.7)_42%,rgba(0,40,26,0.15)_80%)]" />
      <div className="absolute inset-x-0 bottom-0 h-48 bg-gradient-to-t from-[rgba(0,30,20,0.7)] to-transparent" />

      <div className="relative h-full mx-auto max-w-[1400px] px-5 md:px-10 flex flex-col justify-center pt-28">
        <motion.p key={s.label} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="text-[13px] font-bold uppercase tracking-[0.2em] text-[var(--lime)]">
          {s.label} · {s.range}
        </motion.p>
        <MaskLines
          key={s.slug}
          testId="hero-heading"
          delay={0.05}
          className="font-display text-white text-[2.6rem] leading-[1.02] sm:text-6xl lg:text-[5.5rem] mt-5 max-w-4xl"
          lines={[s.lines[0], <span key="i" className="text-[var(--lime)]">{s.lines[1]}</span>]}
        />
        <motion.p key={`sub-${s.slug}`} initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.4 }} className="mt-6 text-base md:text-xl text-[#DCEBE0] max-w-xl leading-relaxed min-h-[5.5em] md:min-h-[4.8em]">
          {s.sub}
        </motion.p>
        <div className="mt-3 flex flex-col sm:flex-row gap-3">
          <Link to={`/products/${s.slug}`} data-testid="hero-explore-button" className="group inline-flex justify-center items-center gap-2 bg-[var(--lime)] text-[var(--forest)] px-7 py-4 rounded-md font-bold transition-transform hover:-translate-y-0.5">
            Explore {s.range} <ArrowRight size={18} className="transition-transform group-hover:translate-x-1" />
          </Link>
          <Link to="/contact" data-testid="hero-quote-button" className="inline-flex justify-center items-center gap-2 border-2 border-white/80 text-white px-7 py-4 rounded-md font-bold transition-colors hover:bg-white hover:text-[var(--forest)]">
            Request a quote
          </Link>
        </div>
      </div>

      <div className="absolute inset-x-0 bottom-0">
        <div className="mx-auto max-w-[1400px] px-5 md:px-10 grid grid-cols-3 gap-2 md:gap-6 pb-6 md:pb-8">
          {SLIDES.map((sl, k) => (
            <button key={sl.slug} data-testid={`hero-slide-${sl.slug}`} onClick={() => setI(k)} className="text-left group">
              <span className="block h-[3px] bg-white/25 overflow-hidden">
                {k === i && <motion.span key={`p${i}`} initial={{ scaleX: 0 }} animate={{ scaleX: 1 }} transition={{ duration: DURATION / 1000, ease: "linear" }} className="block h-full bg-[var(--lime)] origin-left" />}
              </span>
              <span className={`block mt-3 font-display text-sm md:text-lg transition-colors ${k === i ? "text-white" : "text-white/60 group-hover:text-white"}`}>{sl.range}</span>
              <span className="hidden md:block text-[13px] text-white/70">{sl.text}</span>
            </button>
          ))}
        </div>
      </div>
    </section>
  );
};
